import * as React from 'react';
import { DraggableList, DraggableListProps, DraggableListDataItem } from '@common/components/draggable-list';
import { Field, FieldItem } from './component/field';
import { MessageConfirmParams } from '@common/hooks/message';
import cx from 'classnames';

import './fields.styles.scss';

export type FieldsProps<Item extends FieldItem> = {
  disabled?: boolean;
  data: Array<Item>;
  hasError?: (field: Item) => boolean;
  getClassName?: (field: Item) => string;
  canChangeFieldOrder?: (field: Item) => boolean;
  canShowHideField?: (field: Item) => boolean;
  canDeleteField?: (field: Item) => boolean;
  canEditField?: (field: Item) => boolean;
  showDeleteConfirmMessage?: (props: MessageConfirmParams) => void;
  onOrderChange: (data: Array<Item>) => void;
  onEdit: (field: Item) => void;
  onDelete?: (field: Item) => void;
  onShowStatusChange?: (field: Item, status: boolean) => void;
};

export function Fields<T extends FieldItem>(props: FieldsProps<T>) {
  const { 
    disabled,
    data, 
    onOrderChange, 
    onEdit, 
    onDelete, 
    onShowStatusChange, 
    showDeleteConfirmMessage,
    canShowHideField, 
    canEditField, 
    canDeleteField,
    canChangeFieldOrder,
    getClassName,
    hasError,
  } = props;

  const draggableFields = React.useMemo(() => {
    if (!canChangeFieldOrder) {
      return data;
    }

    return data.filter(canChangeFieldOrder);
  }, [data, canChangeFieldOrder]);

  const nonDraggableFields = React.useMemo(() => {
    if (!canChangeFieldOrder) {
      return [];
    }

    return data.filter((x) => !canChangeFieldOrder(x));
  }, [data, canChangeFieldOrder]);

  const handleOrderChange = React.useCallback((newFieldsOrder: Array<T>) => {
    onOrderChange([
      ...nonDraggableFields,
      ...newFieldsOrder,
    ]);
  }, [onOrderChange, nonDraggableFields]);

  const renderItem = React.useCallback((field: T) => {
    return (
      <Field<T>
        data={field}
        features={{
          delete: canDeleteField ? canDeleteField(field) : true,
          edit: canEditField ? canEditField(field) : true,
          changeShowStatus: canShowHideField ? canShowHideField(field) : true
        }}
        showDeleteConfirmMessage={showDeleteConfirmMessage}
        onDelete={onDelete}
        onEdit={onEdit}
        onShowStatusChange={onShowStatusChange}
      />
    );
  }, [
    onDelete, 
    onEdit, 
    onShowStatusChange, 
    showDeleteConfirmMessage, 
    canShowHideField, 
    canEditField, 
    canDeleteField
  ]);

  const handleGetItemClassName = React.useCallback((item: DraggableListDataItem) => {
    const customClass = getClassName ? getClassName(item as T) : '';
    const errorClass = hasError && hasError(item as T) ? 'FhqDesignToolFields__item--has-error' : '';

    return `FhqDesignToolFields__item ${customClass} ${errorClass}`;
  }, [getClassName, hasError]);
  
  return (
    <>
      {nonDraggableFields.length > 0 && (
        <div className={cx("FhqDesignToolFields", { 'FhqDesignToolFields--disabled': disabled })}>
          {nonDraggableFields.map((x) => (
            <div key={x.id} className={handleGetItemClassName(x)}>
              {renderItem(x)}
            </div>
          ))}
        </div>
      )}
      {draggableFields.length > 0 && (
        <DraggableList
          classes={{
            item: handleGetItemClassName,
          }}
          items={draggableFields}
          className={cx("FhqDesignToolFields", { 'FhqDesignToolFields--disabled': disabled })}
          renderItem={renderItem}
          onChange={handleOrderChange as DraggableListProps['onChange']}
        />
      )}
    </>
  );
};